import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  findDocuments,
  getProjects,
  getSession,
  type DocumentFindResult,
  type ProjectSummary,
} from '../api.js';
import { useChrome } from '../chrome.js';
import { hubBaseUrl } from '../config.js';
import { useSession } from '../useSession.js';
import { canCreateProject, canRetrieveDocument } from '../roles.js';
import { DocumentHits } from '../components/DocumentHits.js';
import { retrieveDocument } from '../retrieval.js';
import { signInPathFor } from '../return-path.js';

type Load = { status: 'loading' } | { status: 'ready'; projects: ProjectSummary[] };

/**
 * How long to wait after the last keystroke before asking the server (ms).
 *
 * ⚠️ A BUDGET, NOT A FEEL. R9 rules that find/open must be "as fast or faster than browsing the
 * drive", and this delay is spent on EVERY search — so it comes straight out of that budget. 180ms is
 * under the ~200ms threshold at which a response stops reading as immediate, while still collapsing a
 * typed word into roughly one request instead of one per character.
 */
const DEBOUNCE_MS = 180;

/** The tenant's living projects — a browsable list that drills into each project's governance view. */
export function ProjectsList() {
  const navigate = useNavigate();
  const session = useSession();
  // Supplied by the application root — `AppHeader` in the Deal Analyzer, `HubHeader` in the Hub.
  // This page is rendered by both and no longer decides which product it belongs to (see chrome.tsx).
  const Chrome = useChrome();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [q, setQ] = useState('');
  /**
   * Which request this is. A slow response for "com" must never overwrite a fast one for "commons" —
   * the classic search race, where the list settles on the answer to a question the user has already
   * finished asking. Only the newest sequence number may write.
   */
  const seq = useRef(0);
  /** Whether to offer record creation at all — the server refuses a viewer either way (`roles.ts`). */
  const canCreate = session.status === 'authed' && canCreateProject(session.claims);
  /**
   * Document hits for the same term (canon R8). Held separately from `load` because the two answers
   * arrive independently and a slow document search must never blank the project list that already
   * settled — they share a box, not a lifecycle.
   */
  const [hits, setHits] = useState<DocumentFindResult | null>(null);
  /** WHICH term `hits` answers. Without it the panel states an empty result for a question the
   *  reader has already moved on from — an assertion about records made from a stale response. */
  const [hitsTerm, setHitsTerm] = useState('');
  const canRetrieve = session.status === 'authed' && canRetrieveDocument(session.claims);

  /**
   * Open a document through the attributable-retrieval path.
   *
   * ⚠️ The tab is opened in this synchronous prologue — BEFORE any `await` — or the browser blocks it
   * as a popup. Identical to the documents panel's own `retrieve`, deliberately: two ways to open a
   * file that behave differently is a way to open a file wrongly.
   */
  async function retrieve(projectCode: string, documentId: string): Promise<void> {
    const outcome = await retrieveDocument(
      {
        openTab: () => window.open('', '_blank'),
        ensureSession: async () => (await getSession()) !== null,
        goTo: (url) => window.location.assign(url),
      },
      projectCode,
      documentId,
    );
    if (outcome === 'signed-out')
      void navigate(signInPathFor(window.location, window.location.origin), { replace: true });
  }

  useEffect(() => {
    if (session.status !== 'authed') return;
    const mine = (seq.current += 1);
    const term = q.trim();
    // The first load is not a search and must not wait: an empty box on mount asks immediately.
    const delay = term === '' ? 0 : DEBOUNCE_MS;
    const timer = setTimeout(() => {
      void getProjects(term).then((projects) => {
        if (seq.current === mine) setLoad({ status: 'ready', projects });
      });
      /*
        The document half of the find verb (R8), on the SAME term and the SAME sequence guard — a
        slow answer for "com" must not overwrite a fast one for "commons" in either list.

        `findDocuments` returns [] below two characters without a round trip, which also clears the
        panel as the box empties rather than leaving the last search's hits under a blank query.
      */
      void findDocuments(term).then((found) => {
        if (seq.current !== mine) return;
        setHits(found);
        setHitsTerm(term);
      });
    }, delay);
    return () => clearTimeout(timer);
  }, [session.status, q]);

  if (session.status !== 'authed') {
    return <div className="app__loading">Loading…</div>;
  }

  const searching = q.trim() !== '';

  return (
    <div className="app">
      {Chrome !== null && <Chrome claims={session.claims} />}
      <main className="app__main">
        <div className="app__intro">
          <h1>Projects</h1>
          <p className="sub">
            Your living project records — each governed through its lifecycle, with a Decision
            Record trail, the releases that approve its spend, and the attested advances.
          </p>
          {/*
            The ADD verb (ADR-0035 R9). Beside find and open, because they are the daily three —
            for the people whose daily three they ARE. `POST /projects` answers a viewer with "a
            viewer cannot create a record" (canon R1: a person opens a pursuit), so for a client
            this button was an invitation to a 403.
          */}
          {canCreate && (
            <button
              type="button"
              className="link-btn"
              onClick={() => void navigate('/projects/new')}
            >
              Open a record
            </button>
          )}
        </div>

        {/*
          ⚠️ THE FIND VERB (ADR-0035 P3, R8: search-to-open in ≤3 interactions). Type, see the result,
          click it — three. It is a plain input rather than a submit form on purpose: a form adds a
          fourth interaction (press Enter) to reach the same place.

          It searches OUR Postgres, not Nexus. `search.hybrid` measures p50 2,465ms / p95 5,217ms on
          310 real seam calls, ~30× every other capability — a two-and-a-half-second median fails R9
          against a SharePoint folder that opens instantly. Content search ("what do the documents
          say?") is a different question and stays on the seam, where that wait is honest.
        */}
        <div className="projsearch">
          <label className="projsearch__label" htmlFor="project-search">
            Find a project
          </label>
          <input
            id="project-search"
            className="projsearch__input"
            type="search"
            autoComplete="off"
            placeholder="Name, project number, sponsor, or street"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {load.status === 'loading' && <div className="app__loading">Loading your projects…</div>}

        {load.status === 'ready' &&
          (load.projects.length === 0 ? (
            searching ? (
              /*
                ⚠️ A SEARCH THAT FOUND NOTHING IS NOT AN EMPTY ACCOUNT. Rendering the "no projects
                yet" copy here would tell someone with fifty projects that they have none, because
                they typed a typo — a zero result reported as a statement about the world rather than
                about the query. It names the term back so the reader can see what was actually asked.
              */
              <div className="notice">
                <strong>No project matches “{q.trim()}”.</strong> Searches project name, number,
                sponsor, and street address. Clear the box to see every project.
              </div>
            ) : (
              /*
                ⚠️ THIS COPY DESCRIBED BEHAVIOUR CANON FORBIDS. It read: "Upload a deal package or run
                an analysis, and a project record is created automatically." That was true of
                create-on-miss, which was removed in #326 for violating canon R-a — "project creation is
                human-initiated through the PEE only; no auto-create". A drop for a project nobody
                created now REFUSES. The empty state was still promising the behaviour we deliberately
                took out, which is worse than saying nothing: a reader follows it, uploads a package,
                and no project appears.

                It used to name the intake ROUTE (`POST /projects`) because there was no control to
                point at — the route had been authed and live since P1 with nothing in the SPA calling
                it. There is one now, so the copy points at the button instead of at an HTTP verb.

                ⚠️ AND THERE ARE NOW TWO OF THEM, because "nothing here yet" means two different
                things. For staff it is a prompt: open the first record. For a client it is a status:
                GGP has not opened one for them, and there is nothing for them to do about it.

                Hiding only the BUTTON was the tempting one-line fix and it is wrong — the staff
                sentence is built to END in that control, so removing it leaves a paragraph trailing
                into a full stop with no verb, which reads as a broken page rather than a considered
                one.
              */
              <div className="notice">
                {canCreate ? (
                  <>
                    <strong>No projects yet.</strong> A project record is created deliberately, by a
                    person — uploading a deal package does not create one. Once a project exists,
                    documents dropped into its folder attach to it and re-underwrite it
                    automatically.{' '}
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => void navigate('/projects/new')}
                    >
                      Open the first record
                    </button>
                    .
                  </>
                ) : (
                  <>
                    <strong>No projects yet.</strong> Your projects appear here once Good Good Plans
                    opens a record for them. Nothing is needed from you.
                  </>
                )}
              </div>
            )
          ) : (
            <ul className="projlist">
              {load.projects.map((p) => {
                /*
                  ⚠️ CANON R5 — "no second-class records". A pursuit carries no number (R-e), and for a
                  day it was therefore un-openable here: the row rendered greyed out because
                  `/projects/:code` could not address a record whose code is null. That WAS the
                  second-class record R5 forbids, created by a button we had just shipped.

                  Now it opens by the record's id, which resolves through the same route
                  (`project-ref.ts`). ⚠️ That invents no identifier — canon D1, whether pursuits get
                  their own ID scheme, is a RESERVED FOUNDER RULING, and the uuid is an address the
                  record already had. It never appears in front of the reader: the row shows the NAME,
                  and the class where a number would go.

                  Keyed on `id` regardless — `key={p.code}` collapses every pursuit onto one null key.
                */
                const ref = p.code ?? p.id;
                const openable = typeof ref === 'string' && ref !== '';
                return (
                  <li key={p.id ?? p.code ?? p.name}>
                    <button
                      type="button"
                      className="projlist__row"
                      disabled={!openable}
                      title={openable ? undefined : 'This record has no address yet.'}
                      onClick={
                        openable
                          ? () => void navigate(`/projects/${encodeURIComponent(ref)}`)
                          : undefined
                      }
                    >
                      <span className="projlist__name">
                        {p.name ?? p.code ?? 'Untitled record'}
                      </span>
                      {/*
                        THE MOST SPECIFIC IDENTIFIER THIS RECORD HAS, in that order.

                        🔴 The fallback used to be the literal word "Pursuit", which is a CLASS, not an
                        identifier. The portfolio import landed thirteen pursuits at once and the list
                        rendered thirteen rows reading "Pursuit" — indistinguishable, and each one had
                        a permanent job number that was simply not being shown.

                        `code` first (a promoted project is known by its number), then `jobNumber`
                        (minted at Established, so every record created since job numbering has one),
                        then the class — which is now only reached by records that predate both.
                      */}
                      <span className="projlist__code">
                        {p.code ??
                          p.jobNumber ??
                          (p.recordClass === 'pursuit' ? 'Pursuit' : 'No number')}
                      </span>
                      <span className="projlist__go" aria-hidden="true">
                        {openable ? '→' : ''}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ))}

        {/*
          The document half of the find verb, under the projects it shares a box with. Below rather
          than above deliberately: a person who typed a project name should see projects first, and
          R8's two-interaction path (type, then click a filename) is unaffected by the order.
        */}
        <DocumentHits
          result={hits}
          term={q}
          resultTerm={hitsTerm}
          canRetrieve={canRetrieve}
          onRetrieve={(projectRef, documentId) => void retrieve(projectRef, documentId)}
          origin={hubBaseUrl}
        />
      </main>
    </div>
  );
}
